/**
 * ChannelingCard.js
 *
 * Componente compuesto predefinido para el editor visual.
 * Renderiza una card completa con imagen/gradiente, título, resumen,
 * badge, canalizador y botón — todo editable desde el Settings Panel.
 *
 * Presets disponibles (usar en Toolbox):
 *   PRESETS.dark      → gradiente azul-morado, fondo oscuro
 *   PRESETS.vibrant   → gradiente rosa-rojo, fondo oscuro
 *   PRESETS.nature    → gradiente verde-cyan, fondo oscuro
 *   PRESETS.light     → fondo blanco, imagen real
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import {
  TextField,
  FormControl,
  FormLabel,
  Typography,
  Divider,
  Switch,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// ─────────────────────────────────────────────────────────────────────────────
// Estilos del Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  settings: {
    padding: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.08em',
    color: '#888',
    textTransform: 'uppercase',
    margin: theme.spacing(1.5, 0, 0.5),
  },
  previewBox: {
    width: '100%',
    height: 40,
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(1),
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Presets — valores por defecto para cada variante del Toolbox
// ─────────────────────────────────────────────────────────────────────────────
export const PRESETS = {
  dark: {
    // Imagen
    useImageUrl: false,
    imageUrl: '',
    gradientStart: '#667eea',
    gradientEnd:   '#764ba2',
    gradientAngle: 135,
    imageLabel:    'Canalización',
    // Contenido
    title:         'Título de la Canalización',
    summary:       'Descripción breve del mensaje espiritual que se comparte en esta canalización.',
    // Badge
    badgeText:      'Jeshua',
    badgeColor:     '#ff5722',
    badgeTextColor: '#ffffff',
    // Canalizador
    channelerName:  'Nombre del Canalizador',
    // Botón
    buttonText:     'Ver Canalización',
    buttonColor:    '#ff5722',
    buttonTextColor:'#ffffff',
    // Card
    cardBackground:   '#2d2d3a',
    cardBorderRadius: 8,
    titleColor:       '#ffffff',
    summaryColor:     '#cccccc',
    footerBackground: '#252533',
  },
  vibrant: {
    useImageUrl: false,
    imageUrl: '',
    gradientStart: '#f093fb',
    gradientEnd:   '#f5576c',
    gradientAngle: 135,
    imageLabel:    'Canalización',
    title:         'El Poder del Amor Incondicional',
    summary:       'Mensaje profundo sobre cómo el amor incondicional transforma nuestras vidas.',
    badgeText:      'María Magdalena',
    badgeColor:     '#e91e63',
    badgeTextColor: '#ffffff',
    channelerName:  'Nombre del Canalizador',
    buttonText:     'Ver Canalización',
    buttonColor:    '#e91e63',
    buttonTextColor:'#ffffff',
    cardBackground:   '#2d1f2d',
    cardBorderRadius: 8,
    titleColor:       '#ffffff',
    summaryColor:     '#cccccc',
    footerBackground: '#251825',
  },
  nature: {
    useImageUrl: false,
    imageUrl: '',
    gradientStart: '#43e97b',
    gradientEnd:   '#38f9d7',
    gradientAngle: 135,
    imageLabel:    'Canalización',
    title:         'Sanación del Corazón',
    summary:       'Una guía espiritual para sanar las heridas del corazón y abrirte al amor verdadero.',
    badgeText:      'Arcángel Rafael',
    badgeColor:     '#00897b',
    badgeTextColor: '#ffffff',
    channelerName:  'Nombre del Canalizador',
    buttonText:     'Ver Canalización',
    buttonColor:    '#00897b',
    buttonTextColor:'#ffffff',
    cardBackground:   '#1a2d2a',
    cardBorderRadius: 8,
    titleColor:       '#ffffff',
    summaryColor:     '#cccccc',
    footerBackground: '#152422',
  },
  light: {
    useImageUrl: true,
    imageUrl: 'https://via.placeholder.com/400x200/e8eaf6/3f51b5?text=Imagen',
    gradientStart: '#667eea',
    gradientEnd:   '#764ba2',
    gradientAngle: 135,
    imageLabel:    '',
    title:         'Título de la Canalización',
    summary:       'Descripción breve del mensaje espiritual que se comparte en esta canalización.',
    badgeText:      'Jeshua',
    badgeColor:     '#3f51b5',
    badgeTextColor: '#ffffff',
    channelerName:  'Nombre del Canalizador',
    buttonText:     'Ver Canalización',
    buttonColor:    '#3f51b5',
    buttonTextColor:'#ffffff',
    cardBackground: '#ffffff',
    cardBorderRadius: 8,
    titleColor:     '#212121',
    summaryColor:   '#757575',
    footerBackground: '#f5f5f5',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export const ChannelingCard = ({
  // Imagen / gradiente
  useImageUrl,
  imageUrl,
  gradientStart,
  gradientEnd,
  gradientAngle,
  imageLabel,
  // Contenido
  title,
  summary,
  // Badge
  badgeText,
  badgeColor,
  badgeTextColor,
  // Canalizador
  channelerName,
  // Botón
  buttonText,
  buttonColor,
  buttonTextColor,
  // Card general
  cardBackground,
  cardBorderRadius,
  titleColor,
  summaryColor,
  footerBackground,
}) => {
  const { connectors: { connect, drag } } = useNode();

  // ── Estilos en línea (sin CSS externo para máxima portabilidad) ────────────
  const cardStyle = {
    backgroundColor: cardBackground,
    borderRadius: cardBorderRadius,
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    outline: '1px dashed transparent',
  };

  const imageStyle = {
    width: '100%',
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '1.1rem',
    fontWeight: 500,
    ...(useImageUrl && imageUrl
      ? {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {
          background: `linear-gradient(${gradientAngle}deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
        }),
  };

  const contentStyle = {
    padding: 20,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  const titleStyle = {
    fontSize: '1.1rem',
    fontWeight: 700,
    marginBottom: 12,
    color: titleColor,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  const summaryStyle = {
    fontSize: '0.9rem',
    color: summaryColor,
    marginBottom: 16,
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    flexGrow: 1,
  };

  const metaBottomStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 6,
    marginTop: 'auto',
  };

  const badgeStyle = {
    display: 'inline-block',
    backgroundColor: badgeColor,
    color: badgeTextColor,
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: '0.75rem',
    fontWeight: 700,
  };

  const channelerStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85rem',
    color: summaryColor,
    gap: 4,
  };

  const footerStyle = {
    backgroundColor: footerBackground,
    borderTop: '1px solid rgba(128,128,128,0.15)',
    padding: 16,
    textAlign: 'center',
  };

  const buttonStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    color: buttonColor,
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 4,
  };

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      style={cardStyle}
    >
      {/* ── Imagen / Gradiente ── */}
      <div style={imageStyle}>
        {(!useImageUrl || !imageUrl) && imageLabel && imageLabel}
      </div>

      {/* ── Contenido ── */}
      <div style={contentStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <p style={summaryStyle}>{summary}</p>

        <div style={metaBottomStyle}>
          <span style={badgeStyle}>{badgeText}</span>
          <div style={channelerStyle}>
            <span
              className="material-icons"
              style={{ fontSize: '1rem', opacity: 0.7 }}
            >
              person
            </span>
            <span>{channelerName}</span>
          </div>
        </div>
      </div>

      {/* ── Footer / Botón ── */}
      <div style={footerStyle}>
        <button style={buttonStyle}>
          <span
            className="material-icons"
            style={{ fontSize: '1.2rem' }}
          >
            visibility
          </span>
          {buttonText}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const ChannelingCardSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    useImageUrl,
    imageUrl,
    gradientStart,
    gradientEnd,
    gradientAngle,
    imageLabel,
    title,
    summary,
    badgeText,
    badgeColor,
    badgeTextColor,
    channelerName,
    buttonText,
    buttonColor,
    buttonTextColor,
    cardBackground,
    cardBorderRadius,
    titleColor,
    summaryColor,
    footerBackground,
  } = useNode((node) => ({ ...node.data.props }));

  return (
    <div className={classes.settings}>

      {/* ── IMAGEN ──────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Imagen</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Usar URL de imagen</Typography>
        <Switch
          size="small"
          checked={!!useImageUrl}
          onChange={(e) => setProp((p) => (p.useImageUrl = e.target.checked))}
        />
      </div>

      {useImageUrl ? (
        <FormControl fullWidth margin="dense">
          <FormLabel>URL de imagen</FormLabel>
          <TextField
            size="small"
            value={imageUrl || ''}
            onChange={(e) => setProp((p) => (p.imageUrl = e.target.value))}
            placeholder="https://ejemplo.com/imagen.jpg"
          />
        </FormControl>
      ) : (
        <>
          <FormControl fullWidth margin="dense">
            <FormLabel>Color inicio gradiente</FormLabel>
            <div
              className={classes.previewBox}
              style={{
                background: `linear-gradient(${gradientAngle}deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
              }}
            />
            <TextField
              type="color"
              size="small"
              value={gradientStart || '#667eea'}
              onChange={(e) => setProp((p) => (p.gradientStart = e.target.value))}
            />
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Color fin gradiente</FormLabel>
            <TextField
              type="color"
              size="small"
              value={gradientEnd || '#764ba2'}
              onChange={(e) => setProp((p) => (p.gradientEnd = e.target.value))}
            />
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Ángulo gradiente (°)</FormLabel>
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 0, max: 360 }}
              value={gradientAngle ?? 135}
              onChange={(e) => setProp((p) => (p.gradientAngle = parseInt(e.target.value)))}
            />
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Texto sobre la imagen</FormLabel>
            <TextField
              size="small"
              value={imageLabel || ''}
              onChange={(e) => setProp((p) => (p.imageLabel = e.target.value))}
              placeholder="Ej: Canalización 1"
            />
          </FormControl>
        </>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CONTENIDO ───────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Contenido</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Título</FormLabel>
        <TextField
          size="small"
          value={title || ''}
          onChange={(e) => setProp((p) => (p.title = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Resumen</FormLabel>
        <TextField
          size="small"
          multiline
          rows={3}
          value={summary || ''}
          onChange={(e) => setProp((p) => (p.summary = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color título</FormLabel>
        <TextField
          type="color"
          size="small"
          value={titleColor || '#ffffff'}
          onChange={(e) => setProp((p) => (p.titleColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color resumen</FormLabel>
        <TextField
          type="color"
          size="small"
          value={summaryColor && summaryColor.startsWith('#') ? summaryColor : '#cccccc'}
          onChange={(e) => setProp((p) => (p.summaryColor = e.target.value))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── BADGE ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Badge</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Texto del badge</FormLabel>
        <TextField
          size="small"
          value={badgeText || ''}
          onChange={(e) => setProp((p) => (p.badgeText = e.target.value))}
          placeholder="Ej: Jeshua"
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de fondo badge</FormLabel>
        <TextField
          type="color"
          size="small"
          value={badgeColor || '#ff5722'}
          onChange={(e) => setProp((p) => (p.badgeColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color texto badge</FormLabel>
        <TextField
          type="color"
          size="small"
          value={badgeTextColor || '#ffffff'}
          onChange={(e) => setProp((p) => (p.badgeTextColor = e.target.value))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CANALIZADOR ─────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Canalizador</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Nombre del canalizador</FormLabel>
        <TextField
          size="small"
          value={channelerName || ''}
          onChange={(e) => setProp((p) => (p.channelerName = e.target.value))}
          placeholder="Ej: María González"
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── BOTÓN ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Botón</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Texto del botón</FormLabel>
        <TextField
          size="small"
          value={buttonText || ''}
          onChange={(e) => setProp((p) => (p.buttonText = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color del botón</FormLabel>
        <TextField
          type="color"
          size="small"
          value={buttonColor || '#ff5722'}
          onChange={(e) => setProp((p) => (p.buttonColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color texto botón</FormLabel>
        <TextField
          type="color"
          size="small"
          value={buttonTextColor || '#ffffff'}
          onChange={(e) => setProp((p) => (p.buttonTextColor = e.target.value))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CARD ────────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Card</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de fondo de la card</FormLabel>
        <TextField
          type="color"
          size="small"
          value={cardBackground && cardBackground.startsWith('#') ? cardBackground : '#2d2d3a'}
          onChange={(e) => setProp((p) => (p.cardBackground = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color fondo footer</FormLabel>
        <TextField
          type="color"
          size="small"
          value={footerBackground && footerBackground.startsWith('#') ? footerBackground : '#252533'}
          onChange={(e) => setProp((p) => (p.footerBackground = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Radio de bordes (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 0, max: 40 }}
          value={cardBorderRadius ?? 8}
          onChange={(e) =>
            setProp((p) => (p.cardBorderRadius = parseInt(e.target.value)))
          }
        />
      </FormControl>

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// craft config — usa el preset dark como defaults
// ─────────────────────────────────────────────────────────────────────────────
ChannelingCard.craft = {
  displayName: 'Card Canalización',
  props: { ...PRESETS.dark },
  related: {
    settings: ChannelingCardSettings,
  },
};